import { DealTypes, TimelineTypes } from 'api';
import { Timeline } from 'components';
import React, { Fragment, useMemo, useState } from 'react';
import {
  alpha,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  lighten,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { CloseOutlined, CommentOutlined } from '@mui/icons-material';
import { QueryObserverResult } from '@tanstack/react-query';
import { getFormattedDateFromNow } from 'helpers/dayJsFormats';

interface Props {
  data: DealTypes.ItemCondensed;
  refetch: () => Promise<QueryObserverResult>;
}

const EventCommentDialog = (props: Props): JSX.Element | null => {
  const { data, refetch } = props;

  const [isOpen, setIsOpen] = useState(false);

  const handleOpenDialog = (e: React.MouseEvent) => {
    e.stopPropagation();

    setIsOpen(true);
  };

  const handleCloseDialog = () => {
    setIsOpen(false);
    refetch();
  };

  const events = useMemo(
    () =>
      data.timeline_events?.filter(
        (e) =>
          e.type === TimelineTypes.TypeEnum.Comment ||
          e.type === TimelineTypes.TypeEnum.Popup,
      ),
    [data.timeline_events],
  );

  if (!events || events.length === 0) {
    return null;
  }

  return (
    <>
      <Tooltip
        placement="right"
        title={
          <List dense sx={{ width: '300px', p: '8px' }}>
            {events.map((t, index) => (
              <Fragment key={t._id.$oid}>
                <ListItem disableGutters key={t._id.$oid}>
                  <ListItemText
                    sx={{ m: 0 }}
                    primary={t.comment_text}
                    secondary={`${t.author}, ${getFormattedDateFromNow(
                      t.timestamp,
                    )}`}
                    secondaryTypographyProps={{
                      sx: (theme) => ({
                        color: lighten(theme.palette.grey.A700, 0.8),
                      }),
                    }}
                  />
                </ListItem>
                {index !== events.length - 1 && (
                  <Divider component="li" sx={{ width: 'calc(100% - 16px)' }} />
                )}
              </Fragment>
            ))}
            {events.length === 0 && (
              <ListItem disableGutters>
                <ListItemText sx={{ m: 0 }} primary="No comments yet" />
              </ListItem>
            )}
          </List>
        }
        sx={{
          backgroundColor: 'red',
        }}
        slotProps={{
          tooltip: {
            sx: (theme) => ({
              backgroundColor: alpha(theme.palette.grey.A700, 0.8),
            }),
          },
        }}
      >
        <Button
          onClick={handleOpenDialog}
          size="small"
          sx={{
            minWidth: 'unset',
            padding: '6px',
            borderRadius: '8px',
          }}
        >
          <CommentOutlined fontSize="small" />
        </Button>
      </Tooltip>
      <Dialog open={isOpen} onClose={handleCloseDialog} maxWidth="lg">
        <DialogTitle>
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            wrap="nowrap"
          >
            <Grid item xs={10}>
              <Typography variant="h5" component="p">
                Comments
              </Typography>
            </Grid>
            <Grid item xs="auto">
              <IconButton onClick={handleCloseDialog}>
                <CloseOutlined />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent sx={{ minWidth: '600px' }}>
          <Timeline
            id={data.deal_id}
            type={TimelineTypes.ModelEnum.Deal}
            commentType="popup"
            commentsHeight="50vh"
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EventCommentDialog;
